import { useState } from 'react';
import { httpClient } from '../api/http';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@piggytechng/component-library';
import { ApiResponse } from '../types';
import { authPayload, encryptPassword } from '../helpers/utils/auth-payload';

const useSiteconfig = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [siteMaxMinAmount, setSiteMaxMinAmount] = useState({
    type: '',
    amount: 100,
  });

  const [withdrawalLimits, setWithdrawalLimits] = useState<{
    pocket: number;
    bank: number;
  }>();

  const {
    data: siteConfigMaxMinAmount,
    isLoading: isLoadingSiteConfigMaxMinAmount,
    refetch: refetchSiteConfigMaxMinAmount,
  } = useQuery({
    queryKey: ['site-configuration/withdrawal-threshold'],
    queryFn: () => httpClient.get(`admin/site-configuration/withdrawal-threshold`).then((res) => res.data.data),
  });

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent non-digit characters
    const rawValue = e.target.value.replace(/\D/g, '');

    // Format with commas
    const formattedValue = rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    setSiteMaxMinAmount((prev: any) => ({
      ...prev,
      amount: formattedValue,
    }));
  };

  const setMaxMinThresholdFn = async (password: string) => {
    setIsLoading(true);

    const amountWithoutComma = parseInt(siteMaxMinAmount.amount.toString().replace(/,/g, ''), 10);

    try {
      const response: ApiResponse = await httpClient.post('admin/site-configuration/withdrawal-threshold/update', {
        type: siteMaxMinAmount.type,
        amount: amountWithoutComma,
        password: encryptPassword(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Withdrawal threshold updated successfully');
        setSiteMaxMinAmount({ type: '', amount: 100 });
        refetchSiteConfigMaxMinAmount();
      }
    } catch (error) {
      console.error('Error setting max/min threshold:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    data: withdrawalLimitsData,
    isLoading: isLoadingsWithdrawalLimtsData,
    refetch: refetchWithdrawalLimitsData,
  } = useQuery({
    queryKey: ['site-configuration/withdrawal-limit'],
    queryFn: () => httpClient.get(`admin/site-configuration/withdrawal-limit`).then((res) => res.data.data),
  });

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>, mode: 'pocket' | 'bank') => {
    // Prevent non-digit characters
    const rawValue = e.target.value.replace(/\D/g, '');

    const payload: any = {};

    mode === 'pocket' && (payload.pocket = rawValue);

    mode === 'bank' && (payload.bank = rawValue);

    setWithdrawalLimits((prev: any) => ({
      ...prev,
      ...payload,
    }));
  };

  const setWihdrawalLimitFn = async (password: string) => {
    setIsLoading(true);

    try {
      const response: ApiResponse = await httpClient.post('admin/site-configuration/withdrawal-limit/update', {
        ...withdrawalLimits,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Withdrawal limits updated successfully');
        setSiteMaxMinAmount({ type: '', amount: 100 });
        refetchWithdrawalLimitsData();
      }
    } catch (error) {
      console.error('Error setting max/min threshold:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    siteMaxMinAmount,
    siteConfigMaxMinAmount,
    isLoadingSiteConfigMaxMinAmount,
    setSiteMaxMinAmount,
    setMaxMinThresholdFn,
    handleAmountChange,

    withdrawalLimitsData,
    withdrawalLimits,
    handleLimitChange,
    isLoadingsWithdrawalLimtsData,
    setWihdrawalLimitFn,
  };
};

export default useSiteconfig;
